import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mentorshipApi } from "../lib";
import { showError } from "../lib/toast";
import {
  Video,
  Home,
  CheckCircle,
  Sparkles,
  Calendar,
  Clock,
  MessageCircle,
  PartyPopper,
} from "lucide-react";

const MentorshipSessionSuccessPage = () => {
  const location = useLocation() as any;
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [fireworks, setFireworks] = useState<{ id: number; x: number; y: number }[]>([]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fmtXAF = (amount: number) =>
    new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);

  useEffect(() => {
    const loadSession = async () => {
      const pathname = location.pathname;
      const sessionId = pathname.split("/")[2];

      if (!sessionId) {
        setError("Invalid session ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await mentorshipApi.getSessionDetails(sessionId);
        if (response.success && response.data) {
          setSession(response.data);
        } else {
          throw new Error("Session not found");
        }
      } catch (err: any) {
        setError(err.message || "Failed to load session details");
        showError(err.message || "Failed to load session details");
      } finally {
        setLoading(false);
      }
    };

    loadSession();

    const interval = setInterval(() => {
      if (showConfetti) {
        const newFirework = {
          id: Date.now() + Math.random(),
          x: Math.random() * 100,
          y: Math.random() * 60 + 20,
        };
        setFireworks(prev => [...prev.slice(-5), newFirework]);
        setTimeout(() => {
          setFireworks(prev => prev.filter(f => f.id !== newFirework.id));
        }, 1000);
      }
    }, 2000);

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 6000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [location, showConfetti]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="px-4 text-center">
          <div className="relative">
            <div className="w-12 h-12 mx-auto mb-3 border-4 rounded-full sm:w-16 sm:h-16 sm:mb-4 border-primary border-t-transparent animate-spin" />
            <Sparkles className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 sm:w-6 sm:h-6 text-primary top-1/2 left-1/2 animate-pulse" />
          </div>
          <p className="text-xs text-gray-600 sm:text-sm">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="w-full max-w-md p-5 text-center bg-white shadow-xl sm:p-8 rounded-2xl">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-red-100 rounded-full sm:w-20 sm:h-20 sm:mb-4">
            <svg className="w-8 h-8 text-red-500 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mb-2 text-lg font-bold text-gray-900 sm:text-xl">Something went wrong</h2>
          <p className="mb-5 text-xs text-gray-600 sm:text-sm sm:mb-6">{error}</p>
          <div className="flex flex-col justify-center gap-2 sm:flex-row sm:gap-3">
            <button
              onClick={() => navigate("/mentorship")}
              className="px-4 py-2 sm:px-5 sm:py-2.5 bg-primary text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              Browse Mentors
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 sm:px-5 sm:py-2.5 bg-gray-200 text-gray-800 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute bg-green-200 rounded-full -top-40 -right-40 w-60 h-60 sm:w-80 sm:h-80 opacity-20 animate-pulse" />
        <div className="absolute delay-1000 rounded-full -bottom-40 -left-40 w-60 h-60 sm:w-80 sm:h-80 bg-emerald-200 opacity-20 animate-pulse" />
        <div className="absolute w-64 h-64 -translate-x-1/2 -translate-y-1/2 bg-teal-200 rounded-full top-1/2 left-1/2 sm:w-96 sm:h-96 opacity-10 animate-pulse delay-2000" />
      </div>

      {showConfetti && (
        <div className="fixed inset-0 z-10 pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 6 + 3}px`,
                height: `${Math.random() * 6 + 3}px`,
                backgroundColor: `hsl(${Math.random() * 360}, 80%, 60%)`,
                borderRadius: Math.random() > 0.5 ? "50%" : "0",
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
                opacity: 0.7,
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          ))}
        </div>
      )}

      <div className="absolute inset-0 pointer-events-none">
        {[...Array(12)].map((_, i) => {
          const icons = [Sparkles, Calendar, Clock];
          const Icon = icons[Math.floor(Math.random() * icons.length)];
          return (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${3 + Math.random() * 3}s`,
              }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary/20" />
            </div>
          );
        })}
      </div>

      {fireworks.map((firework) => (
        <div
          key={firework.id}
          className="fixed z-20 pointer-events-none animate-firework"
          style={{
            left: `${firework.x}%`,
            top: `${firework.y}%`,
          }}
        >
          <div className="relative">
            <div className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" />
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-0.5 h-0.5 rounded-full animate-explode"
                style={{
                  backgroundColor: `hsl(${i * 45}, 80%, 60%)`,
                  transform: `rotate(${i * 45}deg) translateX(12px)`,
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>
        </div>
      ))}

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8 sm:px-6 sm:py-12">
        <div className="w-full max-w-2xl mx-auto space-y-6 text-center sm:space-y-8">
          <div className="relative">
            <div className="absolute inset-0 animate-ping">
              <div className="w-20 h-20 mx-auto bg-green-400 rounded-full sm:w-24 sm:h-24 opacity-30" />
            </div>
            <div className="absolute inset-0 delay-300 animate-pulse">
              <div className="w-20 h-20 mx-auto bg-green-300 rounded-full sm:w-24 sm:h-24 opacity-20" />
            </div>
            <div className="relative flex items-center justify-center w-20 h-20 mx-auto rounded-full shadow-2xl sm:w-24 sm:h-24 bg-gradient-to-br from-green-500 to-emerald-600 animate-bounce-in">
              <CheckCircle className="text-white w-11 h-11 sm:w-14 sm:h-14" />
            </div>
            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 animate-spin-slow">
              <Sparkles className="w-4 h-4 text-yellow-500 sm:w-5 sm:h-5" />
            </div>
            <div className="absolute -bottom-1 -left-1 sm:-bottom-2 sm:-left-2 animate-spin-slow-reverse">
              <Calendar className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-500" />
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3 animate-slide-up">
              <PartyPopper className="w-6 h-6 sm:w-8 sm:h-8 text-primary animate-bounce" />
              <h1 className="text-2xl font-bold text-transparent sm:text-4xl md:text-5xl bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text animate-gradient">
                Session Booked!
              </h1>
              <PartyPopper className="w-6 h-6 delay-150 sm:w-8 sm:h-8 text-primary animate-bounce" />
            </div>
            
            <div className="space-y-1 delay-100 sm:space-y-2 animate-slide-up">
              <p className="text-sm text-gray-700 sm:text-lg md:text-xl">
                Your mentorship session is confirmed
              </p>
              {session && (
                <div className="relative inline-block max-w-full px-2">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-600/20 blur-xl" />
                  <p className="relative text-base font-bold text-transparent break-words sm:text-xl md:text-2xl bg-gradient-to-r from-primary to-purple-600 bg-clip-text">
                    {session.topic}
                  </p>
                </div>
              )}
            </div>
          </div>

          {session && (
            <div className="p-4 delay-200 sm:p-5 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl animate-slide-up">
              <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <Calendar className="w-4 h-4 text-primary sm:w-5 sm:h-5" />
                  <span className="text-xs text-gray-600 sm:text-sm">
                    {formatDate(session.scheduledAt)}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <Clock className="w-4 h-4 text-primary sm:w-5 sm:h-5" />
                  <span className="text-xs text-gray-600 sm:text-sm">
                    {formatTime(session.scheduledAt)} ({session.duration} min)
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <Video className="w-4 h-4 text-primary sm:w-5 sm:h-5" />
                  <span className="text-xs text-gray-600 sm:text-sm">
                    Video call session
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <MessageCircle className="w-4 h-4 text-primary sm:w-5 sm:h-5" />
                  <span className="text-xs text-gray-600 sm:text-sm">
                    Chat support after session
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 delay-300 shadow-lg sm:p-5 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl animate-slide-up">
            <p className="text-[11px] sm:text-sm italic text-gray-600">
              "Great things are done by a series of small things brought together. 
              Your mentorship journey starts now! 🚀"
            </p>
          </div>

          <div className="flex flex-col justify-center gap-3 sm:gap-4 animate-slide-up delay-400">
            <button
              onClick={() => navigate("/dashboard/student/mentorship-sessions")}
              className="group relative px-5 py-2.5 sm:px-8 sm:py-3.5 bg-gradient-to-r from-primary to-purple-600 text-white text-xs sm:text-sm font-medium rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
            >
              <div className="absolute inset-0 transition-transform duration-700 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-full" />
              <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
                <Video className="w-4 h-4 sm:w-5 sm:h-5" />
                View My Sessions
              </div>
            </button>
            <button
              onClick={() => navigate("/mentorship")}
              className="px-5 py-2.5 sm:px-8 sm:py-3.5 bg-white text-gray-700 text-xs sm:text-sm font-medium rounded-xl border border-gray-200 hover:bg-gray-50 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <Home className="w-4 h-4 sm:w-5 sm:h-5" />
              Find More Mentors
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(5deg);
          }
        }

        @keyframes firework {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        @keyframes explode {
          0% {
            transform: rotate(0deg) translateX(0px);
            opacity: 1;
          }
          100% {
            transform: rotate(0deg) translateX(25px);
            opacity: 0;
          }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes slide-up {
          0% {
            transform: translateY(20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes spin-slow-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }

        .animate-confetti {
          animation: confetti linear forwards;
        }
        .animate-float {
          animation: float ease-in-out infinite;
        }
        .animate-firework {
          animation: firework 0.8s ease-out forwards;
        }
        .animate-explode {
          animation: explode 0.4s ease-out forwards;
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out forwards;
          opacity: 0;
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 4s linear infinite;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-150 { animation-delay: 0.15s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-1000 { animation-delay: 1s; }
        .delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default MentorshipSessionSuccessPage;